import {Notification as PeacockNotification} from '@pepsico-ds/ui'
import {useEffect, useState} from 'react'

/**
 * @typedef {import('@pepsico-ds/ui').INotificationProps} NotificationProps
 * @property {'info' | 'success' | 'warning' | 'error'} type Notification visual type.
 * @property {string | import('react').ReactNode} title Notification title.
 * @property {boolean} [showIcon] When true, renders the notification icon.
 * @property {boolean} [showTitle] When true, renders the notification title.
 * @property {boolean} [showText] When true, renders notification text.
 * @property {string} [text] Notification text content.
 * @property {boolean} [showActionButtons] When true, renders action buttons.
 * @property {string[]} [details] Optional list of detail messages. When provided, a "View details"/"Hide details"
 *   action button toggles `text` to reveal them, instead of relying on the `text`/`showText` props directly.
 * @property {(isExpanded: boolean) => void} [onDetailsToggle] Callback fired when the details toggle is clicked.
 * @property {string} ['data-cy'] Optional Cypress test attribute.
 */

/**
 * Wrapper around Peacock DS Notification.
 * Supports all Peacock DS Notification props, plus an optional expandable `details` list.
 * @param {NotificationProps} props
 */
export const Notification = ({details, onDetailsToggle, title, ...props}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const hasDetails = Array.isArray(details) && details.length > 0

  useEffect(() => {
    setIsExpanded(false)
  }, [title])

  if (!hasDetails) {
    return <PeacockNotification title={title} {...props} />
  }

  const toggleDetails = () => {
    const next = !isExpanded
    setIsExpanded(next)
    onDetailsToggle?.(next)
  }

  return (
    <PeacockNotification
      {...props}
      title={title}
      text={isExpanded ? details.join(' | | \n') : undefined}
      showText={isExpanded}
      showActionButtons
      primaryActionLabel={isExpanded ? 'Hide details' : 'View details'}
      primaryActionOnClick={toggleDetails}
    />
  )
}

